import { Button } from '@/app/common/components/button/Button'
import { Input } from '@/app/common/components/input/Input'
import LoadingCard from '@/app/common/components/LoadingCard'
import Pagination from '@/app/common/components/Pagination'
import { Card, CardContent, CardHeader } from '@/common/components/Card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select'

import { PaginationState } from '@/app/common/usePagination'
import { SystemCatalogCategory } from '@/queries/system_catalog/system_catalog.schema'
import { Plus, Search } from 'lucide-react'
import { useState } from 'react'
import {
  ApprovalRuleFilters,
  approvalTypeFilterSchema,
  createFilterOptions,
  targetTypeFilterSchema,
} from '../hooks/useApprovalRuleFilters'
import {
  ApprovalRule,
  PaginationMeta,
  PaymentRulesConfig,
  statusFilterSchema,
} from '../schema'
import { removeUnderscoresAndMakeFirstLetterCapital } from '../utils'
import { BulkActions } from './bulk-actions-ui'
import {} from './create-rule-form/create-rule-form'
import CreateRuleFormWrapper from './create-rule-form/create-rule-form-wrapper'
import { createColumns, RuleDetailsDialog, RuleTable } from './rule-table'
import EditRuleForm from './rule-table/edit-rule-form'

type Props = {
  categories: SystemCatalogCategory[]
  rules: ApprovalRule[]
  approvalRulesConfig: PaymentRulesConfig
  filters: ApprovalRuleFilters
  rulesIsLoading: boolean
  pagination: PaginationState
  rulesMetadata: PaginationMeta
}

export default function ApprovalRulesDashboard(props: Props) {
  const {
    categories,
    rules,
    approvalRulesConfig,
    filters,
    rulesIsLoading,
    pagination,
    rulesMetadata,
  } = props
  const [selectedRuleIds, setSelectedRuleIds] = useState<number[]>([])

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [selectedRuleIdForDetails, setSelectedRuleIdForDetails] = useState<
    number | null
  >(null)
  const [selectedRuleIdForEdit, setSelectedRuleIdForEdit] = useState<
    number | null
  >(null)

  const openCreateDialog = () => {
    setShowCreateDialog(true)
  }

  const handleViewDetails = (rule: ApprovalRule) => {
    setSelectedRuleIdForDetails(rule.id)
  }

  const handleEditRule = (rule: ApprovalRule) => {
    setSelectedRuleIdForEdit(rule.id)
  }

  const columns = createColumns({
    onEditRule: handleEditRule,
    onViewDetails: handleViewDetails,
  })

  const filterOptions = createFilterOptions(approvalRulesConfig)

  function clearSelectedRuleIds() {
    setSelectedRuleIds([])
  }

  return (
    <div className="">
      <Card>
        <CardHeader className="flex justify-between items-center">
          <div>
            <div className="text-gray-800">Rules Dashboard</div>
            <div className="text-neutral-600 font-normal text-sm">
              Manage and monitor all approval rules
            </div>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Create New Rule
              </Button>
            </DialogTrigger>
            <DialogContent
              onInteractOutside={(e) => {
                e.preventDefault()
              }}
              className="max-w-md"
            >
              <DialogHeader>
                <DialogTitle>{'Create New Rule'}</DialogTitle>
                <DialogDescription>
                  {'Configure a new approval rule for purchases'}
                </DialogDescription>
              </DialogHeader>
              <CreateRuleFormWrapper
                approvalRulesConfig={approvalRulesConfig}
                onCancel={() => {
                  setShowCreateDialog(false)
                }}
                onCreateSuccess={() => {
                  setShowCreateDialog(false)
                }}
                categories={categories}
              />
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {/* Filters and Search */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search rules or targets..."
                value={pagination.search}
                onChange={(e) => {
                  pagination.onSearchChange(e.target.value)
                }}
                className="pl-10"
              />
            </div>
            <Select
              value={filters.values.targetType}
              onValueChange={(value) => {
                const narrowType = targetTypeFilterSchema.safeParse(value)
                if (narrowType.success) {
                  filters.actions.setTargetType(narrowType.data)
                }
                clearSelectedRuleIds()
              }}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Target Type" />
              </SelectTrigger>
              <SelectContent>
                {filterOptions.targetType.map((option) => (
                  <SelectItem key={option.id} value={option.name}>
                    {removeUnderscoresAndMakeFirstLetterCapital(option.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.values.approvalType}
              onValueChange={(value) => {
                const narrowType = approvalTypeFilterSchema.safeParse(value)
                if (narrowType.success) {
                  filters.actions.setApprovalType(narrowType.data)
                }
                clearSelectedRuleIds()
              }}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Approval Type" />
              </SelectTrigger>
              <SelectContent>
                {filterOptions.approvalType.map((option) => (
                  <SelectItem key={option.id} value={option.name}>
                    {removeUnderscoresAndMakeFirstLetterCapital(option.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.values.status}
              onValueChange={(value) => {
                const narrowType = statusFilterSchema.safeParse(value)
                if (narrowType.success) {
                  filters.actions.setStatus(narrowType.data)
                }
                clearSelectedRuleIds()
              }}
            >
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {filterOptions.status.map((option) => (
                  <SelectItem key={option.id} value={option.name}>
                    {removeUnderscoresAndMakeFirstLetterCapital(option.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <BulkActions selectedRuleIds={selectedRuleIds} rules={rules} />

          {/* Rules Table */}

          {rulesIsLoading ? (
            <LoadingCard title="Loading Rules" />
          ) : (
            <>
              <RuleTable
                columns={columns}
                data={rules}
                onRowSelectionChange={(selectedRows) => {
                  const selectedIds = selectedRows.map((rule) => rule.id)
                  setSelectedRuleIds(selectedIds)
                }}
                selectedRows={selectedRuleIds}
              />
            </>
          )}
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="space-x-2">
              <Pagination
                currentPage={pagination.currentPage}
                itemsPerPage={pagination.perPage}
                totalRows={rulesMetadata.total}
                onPageChange={(page) => {
                  pagination.setCurrentPage(page)
                  clearSelectedRuleIds()
                }}
                onPerPageChange={(perPage) => {
                  pagination.setPerPage(perPage)
                  clearSelectedRuleIds()
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <EditRuleForm
        selectedRuleId={selectedRuleIdForEdit}
        rules={rules}
        onOpenChange={() => {
          setSelectedRuleIdForEdit(null)
        }}
        onEditSuccess={() => {
          setSelectedRuleIdForEdit(null)
        }}
        onCancel={() => {
          setSelectedRuleIdForEdit(null)
        }}
        approvalRulesConfig={approvalRulesConfig}
        categories={categories}
      />

      {/* Rule Details Dialog */}
      <RuleDetailsDialog
        selectedRuleId={selectedRuleIdForDetails}
        rules={rules}
        onOpenChange={(open: boolean) => {
          if (!open) setSelectedRuleIdForDetails(null)
        }}
      />
    </div>
  )
}
